/**
 * 1_AsdPADOutcome.js — Assessment Outcome (step 1 of the Adult Autism PAD)
 *
 * The opening (and, for a Not-met outcome, the only) step of the Post-Assessment
 * Discussion / "Feedback Session" module. It captures what was discussed when
 * the outcome was fed back to the patient:
 *
 *   • A short list of discussion points, rendered as ADHD-style selectable
 *     pills. Two are always shown; two more ("strengths" / "concerns") appear
 *     only when the patient Met criteria — mirroring the ADHD PAD's conditional
 *     questions.
 *   • The person's reflection (free-text → StructuredNotesEditor, optional).
 *   • The person's goals (free-text → StructuredNotesEditor) — shown and REQUIRED
 *     only when the outcome is Met, because they feed the patient-goals paragraph.
 *
 * The DD outcome is read live from assessmentStages.diagnosticDecision; PAD never
 * stores its own copy (so a late DD change is always reflected).
 *
 * Persistence: a single hydration-guarded writer effect persists the discussed
 * flags + both note arrays + the recomputed progress fraction, via the debounced
 * updateAssessmentStage API. The same unmount-regeneration safety-net used by the
 * DD decision page guarantees the "Assessment Outcome" report section is
 * regenerated if the clinician leaves via an unmanaged exit mid-edit.
 *
 * Finish flow:
 *   - Met:     the footer shows "Next: Next Steps" (gated on goals being present);
 *              the actual finish + generation happens on the Next Steps page.
 *   - Not met: the footer shows "Finish Feedback Session" (useAutismPADCompletion),
 *              which completes the stage and generates the Assessment Outcome.
 *              When differentials decisions are disabled (TOC) and ADHD is an
 *              emerging differential, a cross-condition follow-up toggle is
 *              rendered so the clinician can record whether the report should
 *              include ADHD follow-up instructions. The toggle writes to
 *              data.nextSteps.includeAdhdFollowUp (same Firestore path as the
 *              Next Steps page) via a targeted standalone write, and must be
 *              answered before Finish is enabled.
 */

import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon, CheckIcon, PlusIcon } from '@heroicons/react/solid';
import Layout from '../../../components/Layout';
import Breadcrumb from '../../../components/Breadcrumb';
import PreviewModeBanner from '../../../components/PreviewModeBanner';
import ModularSideNavbar from '../../../components/autismModular/ModularSideNavbar';
import ModularStepFooter from '../../../components/autismModular/ModularStepFooter';
import StructuredNotesEditor, { normaliseStructuredNotes } from '../12_observation/components/StructuredNotesEditor';
import AutismSupportingDocumentsPanel from '../../../components/autismReview/AutismSupportingDocumentsPanel';
import AutismPatientGoalsDisplay from '../../../components/autismReview/AutismPatientGoalsDisplay';
import AutismDecisionCard from '../../../components/autismReview/AutismDecisionCard';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { ModularPatientDataContext } from '../../../context/ModularPatientDataContext';
import { useIsPreviewMode } from '../../../hooks/useIsPreviewMode';
import { usePreviewAwareNavigate } from '../../../hooks/usePreviewAwareNavigate';
import { useCanEditStage } from '../../../hooks/useCanEditStage';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import PatientContractTypeBadge from '../../../components/PatientContractTypeBadge';
import { useAutismPADCompletion } from '../../../hooks/useAutismPADCompletion';
import { useModularSectionExitRegeneration } from '../../../hooks/useModularSectionExitRegeneration';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { getCombinedEmergingDifferentials } from '../../../utils/autismDDRouting';
import { asdLog, summariseNotes } from '../../../utils/autismDebugLog';
import { auth } from '../../../firebase';
import {
  AUTISM_PAD_STAGE_KEY,
  AUTISM_PAD_REPORT_STAGE,
  AUTISM_PAD_OUTCOME_SECTION,
  AUTISM_PAD_DISPLAY_NAME,
  getAutismPADStepRoutes,
  getAutismDiagnosisOutcome,
  isNextStepsApplicable,
  isAssessmentOutcomeComplete,
  computeAutismPADProgress,
  buildAutismPADNavSections,
} from '../../../utils/autismPADRouting';

/**
 * The discussion-point checkboxes. `metOnly` items are only shown (and only
 * count toward the record) when the outcome is Met — exactly mirroring the ADHD
 * PAD's conditionsToShow pattern.
 */
const DISCUSSED_ITEMS = [
  { key: 'explainedOutcome', label: 'Explained the assessment outcome to the patient' },
  { key: 'validatedExperiences', label: 'Validated the patient\u2019s experiences' },
  { key: 'highlightedStrengths', label: 'Highlighted the patient\u2019s strengths', metOnly: true },
  { key: 'addressedConcerns', label: 'Addressed the patient\u2019s concerns and questions', metOnly: true },
];

export default function AsdPADOutcome() {
  const { patientId } = useParams();
  const navigate = usePreviewAwareNavigate();

  const {
    header,
    formInstances,
    assessmentStages,
    updateAssessmentStage,
    flushStageWrites,
    reportContent,
  } = useContext(ModularPatientDataContext);

  const isPreviewMode = useIsPreviewMode();
  const canEdit = useCanEditStage(header || {}, 'PAD', { previewMode: isPreviewMode });

  // Clinician's organisation gates the contract-type badge (only orgs that
  // categorise patients by contract type surface it). Value comes from the
  // patient header. Read-only, so it renders in preview mode too.
  const { claims } = useAuth();

  // Workflow config drives whether the cross-condition follow-up toggle is
  // shown. When differentials decisions are disabled (TOC / Demo Org), the old
  // per-condition ADHD referral logic in the DD module is unavailable, so the
  // clinician decides on this page (negative path) or Next Steps (positive path)
  // whether the report should include ADHD follow-up instructions.
  const wf = useWorkflowConfig(header, claims);

  const padStage = assessmentStages?.[AUTISM_PAD_STAGE_KEY];
  const savedOutcomeData = padStage?.data?.assessmentOutcome || {};

  // Live DD outcome — PAD branches on this but never stores its own copy.
  const outcome = getAutismDiagnosisOutcome(assessmentStages);
  const isMet = isNextStepsApplicable(outcome);

  // ── ADHD Follow-Up gating (negative-outcome path only) ─────────────────────
  // Mirrors the logic on 2_AsdPADNextSteps.js, but the panel only renders when
  // the outcome is Not-met (when Met, the toggle lives on the Next Steps page).
  // Uses the same case-insensitive match as the backend _compute_adhd_referral_key.
  const isAdhdEmergingDifferential = useMemo(() => {
    const differentials = getCombinedEmergingDifferentials(assessmentStages);
    return differentials.some((name) => name.toLowerCase().includes('adhd'));
  }, [assessmentStages]);

  // Show the panel on the Not-met path ONLY when differentials decisions are
  // disabled AND ADHD is actually an emerging differential. On the negative
  // path the panel is entirely hidden when ADHD is not a differential (unlike
  // the positive-path Next Steps page where it stays visible but greyed out).
  const showAdhdFollowUpPanel = !isMet && wf.skipAutismDifferentialsDecisions && isAdhdEmergingDifferential;
  // On the negative path the panel only renders when active (the show guard
  // already includes the differential check).
  const isAdhdFollowUpActive = showAdhdFollowUpPanel;

  // Seed from the same Firestore path the Next Steps page uses. Both pages write
  // to data.nextSteps.includeAdhdFollowUp so a diagnosis change carries the
  // value seamlessly between the two pages.
  const savedNextSteps = padStage?.data?.nextSteps || {};
  const [includeAdhdFollowUp, setIncludeAdhdFollowUp] = useState(
    () => savedNextSteps.includeAdhdFollowUp === true || savedNextSteps.includeAdhdFollowUp === false
      ? savedNextSteps.includeAdhdFollowUp
      : null,
  );

  // Lazy initial state — never clobbered by later context refreshes.
  const [discussed, setDiscussed] = useState(() => {
    const saved = savedOutcomeData.discussed || {};
    const seed = {};
    for (const { key } of DISCUSSED_ITEMS) seed[key] = saved[key] === true;
    return seed;
  });
  const [reflectionNotes, setReflectionNotes] = useState(
    () => normaliseStructuredNotes(savedOutcomeData.patientReflectionNotes),
  );
  const [goalsNotes, setGoalsNotes] = useState(
    () => normaliseStructuredNotes(savedOutcomeData.patientGoalsNotes),
  );

  // ── Status promotion (notStarted -> inProgress on first write) ──────────────
  const hasStartedRef = useRef(false);
  const maybePromoteStatus = (updates) => {
    if (!hasStartedRef.current && padStage?.status === 'notStarted') {
      hasStartedRef.current = true;
      return { ...updates, status: 'inProgress' };
    }
    return updates;
  };

  // ── Dirty tracking ───────────────────────────────────────────────────────────
  const [hasChanges, setHasChanges] = useState(false);

  // Targeted standalone write for the ADHD follow-up toggle — decoupled from the
  // main writer effect so toggling does not re-persist the discussion flags and
  // notes. Matches the handleRationaleBlur pattern on 2_AsdPADNextSteps.js.
  const handleAdhdFollowUpToggle = (value) => {
    if (!canEdit) return;
    const newVal = includeAdhdFollowUp === value ? null : value;
    setIncludeAdhdFollowUp(newVal);
    updateAssessmentStage(AUTISM_PAD_STAGE_KEY, {
      'data.nextSteps.includeAdhdFollowUp': newVal,
    });
    setHasChanges(true);
  };

  // ── Single-writer persistence effect ───────────────────────────────────────
  // One hydration-guarded effect persists the discussed flags + both note arrays
  // + the recomputed progress fraction. Progress never regresses below 1 once the
  // stage is completed.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!hydratedRef.current) { hydratedRef.current = true; return; }
    if (!canEdit) return;

    const nextData = {
      ...(padStage?.data || {}),
      assessmentOutcome: {
        ...(padStage?.data?.assessmentOutcome || {}),
        discussed,
        patientReflectionNotes: reflectionNotes,
        patientGoalsNotes: goalsNotes,
      },
    };
    let progress = computeAutismPADProgress({ data: nextData, outcome, status: padStage?.status });
    if (padStage?.status === 'completed' && progress < 1) progress = 1;

    updateAssessmentStage(AUTISM_PAD_STAGE_KEY, maybePromoteStatus({
      'data.assessmentOutcome.discussed': discussed,
      'data.assessmentOutcome.patientReflectionNotes': reflectionNotes,
      'data.assessmentOutcome.patientGoalsNotes': goalsNotes,
      progress,
    }));
    asdLog('PAD', 'persist outcome', {
      outcome: outcome || 'undecided',
      progress,
      reflection: summariseNotes(reflectionNotes),
      goals: summariseNotes(goalsNotes),
    });
    setHasChanges(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit, discussed, reflectionNotes, goalsNotes]);

  const handleToggleDiscussed = (key) => {
    if (!canEdit) return;
    setDiscussed(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Goals are mandatory only when Met; reflection is always optional.
  const goalsPresent = isAssessmentOutcomeComplete(
    { assessmentOutcome: { patientGoalsNotes: goalsNotes } },
    outcome,
  );

  // ── Completion (Not-met path only) ──────────────────────────────────────────
  // padStage lets the hook stamp firstCompletedAt write-once; the appointment
  // flag gates the inferred-appointment audit entry (see the hook's docs).
  const padCompletion = useAutismPADCompletion({
    patientId, updateAssessmentStage, flushStageWrites, canEdit,
    padStage, isAppointmentDatesEnabled: wf.isAppointmentDatesEnabled,
  });

  // ── Exit-regeneration via shared hook ────────────────────────────────────────
  // Replaces the hand-rolled unmount net. The hook registers dirty state with
  // ProformaNavigationGuardContext (leaving-section dialog + beforeunload) and
  // fires TriggerGenerateDocumentSection on unmount when dirty or not yet
  // generated. Managed exits call markRegenHandled() so the hook stays out of
  // the way.
  const { markRegenHandled } = useModularSectionExitRegeneration({
    patientId,
    stageKey: AUTISM_PAD_STAGE_KEY,
    sectionTitle: AUTISM_PAD_OUTCOME_SECTION,
    hasChanges,
    canEdit,
    reportContent,
  });

  const flushAndGenerate = async () => {
    markRegenHandled();
    const dataReady = flushStageWrites(AUTISM_PAD_STAGE_KEY);
    if (canEdit) {
      TriggerGenerateDocumentSection(
        patientId, AUTISM_PAD_REPORT_STAGE, AUTISM_PAD_OUTCOME_SECTION, [], dataReady,
      );
    }
    await dataReady;
  };

  const { items: navItems } = useMemo(
    () => buildAutismPADNavSections({ assessmentStages, currentSectionIndex: 0 }),
    [assessmentStages],
  );

  const routes = getAutismPADStepRoutes(patientId);

  const navigateToSection = async (id) => {
    if (id === 'assessmentOutcome') return;
    await flushAndGenerate();
    navigate(routes.nextSteps);
  };

  const handleNext = async () => {
    asdLog('PAD', 'Outcome -> Next Steps (Met)', { goalsPresent });
    await flushAndGenerate();
    navigate(routes.nextSteps);
  };

  // Not-met path: finishing happens here. Mark the regen as handled so the
  // unmount net does not double-fire; the completion hook owns flush + generation.
  const handleFinish = async () => {
    markRegenHandled();
    await padCompletion.handleComplete();
  };

  const handleAfterGeneration = () => {
    navigate(`/patient/${patientId}/adult-autism/documents`);
  };

  const breadcrumbTuples = [
    ['Patient Overview', `/patient/${patientId}/adult-autism`],
    ['Adult Autism Assessment'],
    [AUTISM_PAD_DISPLAY_NAME],
    ['Assessment Outcome'],
  ];

  const visibleDiscussed = DISCUSSED_ITEMS.filter(item => isMet || !item.metOnly);

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* Wizard shell — shared sidebar + scrollable content + fixed footer. */}
      <div className="flex h-[calc(100vh-8rem)]">
        <ModularSideNavbar
          items={navItems}
          activeId="assessmentOutcome"
          onSelect={navigateToSection}
        />

        <div className="flex-1 flex flex-col min-h-0">
          {/* Preview mode info banner — shown when opened via a Preview button */}
          {isPreviewMode && <PreviewModeBanner />}
          {/* Body — cloned from the ADHD PAD "Review of Assessment" page
              (9_postAssessmentDiscussion/1_REV.js): a left-aligned numbered
              section title, the Supporting Documents panel, then full-width
              "p-8 my-8" subsection panels with slate text-xl headers. Only the
              data plumbing (modular context, structured-notes boxes) and the
              brand colour (primary, not violet) differ from the ADHD original. */}
          <div className="flex-1 overflow-y-auto p-8 text-left">
            {/* Heading left, contract-type badge right-aligned via justify-between. */}
            <div className="flex justify-between items-center gap-3 mb-6">
              <h2 className="text-2xl font-bold">1. Assessment Outcome</h2>
              <PatientContractTypeBadge
                label="Contract"
                organisation={claims?.organisation}
                assessmentType={header?.customFields?.assessmentType}
                subContractType={header?.customFields?.subContractType}
              />
            </div>

            {/* Supporting Documents — live previews of the rendered upstream
                Autism reports (History/SSI). Hidden until at least one has a
                rendered PDF. Mirrors the ADHD PAD layout. */}
            <AutismSupportingDocumentsPanel
              patientId={patientId}
              clinicianId={auth.currentUser?.uid}
            />

            {/* 1.1 Summary of Assessment Findings — the discussion points covered
                when the outcome was fed back, rendered as ADHD-style selectable
                pills, plus the patient's reflection notes. */}
            <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
              <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                1.1 Summary of Assessment Findings
              </h3>
              <p className="text-left text-gray-700 mb-6">
                Select each point that was covered when the assessment outcome was fed back to the patient.
              </p>

              <div className="space-y-3">
                {visibleDiscussed.map(({ key, label }) => {
                  const checked = discussed[key] === true;
                  return (
                    <button
                      key={key}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => handleToggleDiscussed(key)}
                      className={`
                        w-full flex items-center gap-3 p-3 rounded-lg text-left
                        transition-all duration-300 ease-in-out
                        ${!canEdit ? 'cursor-not-allowed opacity-75' : 'cursor-pointer group'}
                        ${checked
                          ? 'bg-primary border-2 border-primary shadow-lg'
                          : canEdit
                            ? 'bg-white hover:bg-gray-50 border-2 border-dashed border-gray-300 hover:border-gray-400 shadow-sm hover:shadow-md'
                            : 'bg-white border-2 border-dashed border-gray-300 shadow-sm'
                        }
                      `}
                    >
                      <div className={`flex-shrink-0 transition-all duration-300 ease-in-out ${checked ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'}`}>
                        {checked
                          ? <CheckIcon className="w-5 h-5" aria-hidden="true" />
                          : <PlusIcon className="w-5 h-5" aria-hidden="true" />}
                      </div>
                      <span className={`font-semibold transition-all duration-300 ease-in-out ${checked ? 'text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                        {label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Patient's reflection — optional clinician notes (structured
                  boxes, the Autism equivalent of the ADHD single text area). */}
              <div className="text-left mt-6">
                <h4 className="text-gray-700 text-sm font-semibold mb-2">
                  Capture how the patient responded to and reflected on the assessment outcome
                </h4>
                <StructuredNotesEditor
                  notes={reflectionNotes}
                  onChange={canEdit ? setReflectionNotes : undefined}
                  placeholder="Type a reflection note and press Enter..."
                  readOnlyPlaceholder="No reflection recorded"
                  readOnly={!canEdit}
                  minHeight="8rem"
                  domainId="patientReflectionNotes"
                />
              </div>
            </div>

            {/* 1.2 ADHD Follow-Up (Not-met path only, TOC orgs) ─────────────────
                When differentials decisions are disabled and ADHD was flagged as
                an emerging differential, the clinician records here whether the
                report should include ADHD follow-up instructions. On the Met
                path this toggle lives on the Next Steps page instead. */}
            {showAdhdFollowUpPanel && (
              <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  1.2 ADHD Follow-Up
                </h3>
                <div className="space-y-4">
                  <AutismDecisionCard
                    question="Include ADHD follow-up instructions in the report?"
                    value={isAdhdFollowUpActive ? includeAdhdFollowUp : null}
                    onChange={handleAdhdFollowUpToggle}
                    disabled={!canEdit || !isAdhdFollowUpActive}
                    inactive={!isAdhdFollowUpActive}
                    tooltip={!isAdhdFollowUpActive
                      ? 'This only applies when ADHD is selected as an emerging differential.'
                      : 'When set to Yes, the report will include a boilerplate paragraph advising the patient to seek a follow-up ADHD assessment.'}
                  />
                </div>
              </div>
            )}

            {/* 1.3 Patient's Goals — shown and required only when Met. Mirrors the
                ADHD PAD "Patient's Goals" subsection: the previously-stated goals
                read-only reference block above the clinician goals notes.
                Section numbering shifts to 1.3 when the ADHD Follow-Up panel is
                visible (Not-met path), but this section is Met-only so the two
                are mutually exclusive and the numbering never coexists. */}
            {isMet && (
              <div className="p-8 my-8 bg-gray-50 rounded-2xl border border-gray-200 text-left">
                <h3 className="text-left text-slate-700 text-xl font-semibold mb-6">
                  1.2 Patient&apos;s Goals
                </h3>

                {/* Read-only reference: the patient's own goals as stated at
                    intake ("Your Current Concerns" form). */}
                <AutismPatientGoalsDisplay
                  historyStage={assessmentStages?.history}
                  formInstances={formInstances}
                  patientFirstName={header?.firstName || ''}
                />

                <div className="text-left mt-6">
                  <h4 className="text-gray-700 text-sm font-semibold mb-2">
                    Record the goals the patient would like to work towards following their diagnosis
                    <span className="text-red-600 ml-1">*</span>
                  </h4>
                  <StructuredNotesEditor
                    notes={goalsNotes}
                    onChange={canEdit ? setGoalsNotes : undefined}
                    placeholder="Type a goal and press Enter..."
                    readOnlyPlaceholder="No goals recorded"
                    readOnly={!canEdit}
                    minHeight="8rem"
                    domainId="patientGoalsNotes"
                  />
                </div>
              </div>
            )}
          </div>

          {isMet ? (
            <ModularStepFooter
              showBack={false}
              counterText={`1 of ${navItems.length}`}
              onNext={handleNext}
              nextDisabled={!goalsPresent}
              nextLabel="Next: Next Steps"
            />
          ) : (
            <ModularStepFooter
              showBack={false}
              counterText={`1 of ${navItems.length}`}
              rightActions={canEdit ? (() => {
                // The Finish button is blocked when the ADHD follow-up toggle is
                // active (ADHD is an emerging differential + differentials decisions
                // disabled) but the clinician has not yet answered it.
                const adhdFollowUpBlocking = isAdhdFollowUpActive && includeAdhdFollowUp === null;
                const isFinishDisabled = padCompletion.isSaving || adhdFollowUpBlocking;
                return (
                  <button
                    onClick={handleFinish}
                    disabled={isFinishDisabled}
                    className={`flex items-center gap-2 py-2 px-4 rounded-md shadow-sm transition-all duration-200 text-sm font-semibold whitespace-nowrap ${
                      isFinishDisabled
                        ? 'opacity-60 cursor-not-allowed bg-primary text-white'
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                  >
                    {padCompletion.isSaving ? 'Saving...' : 'Finish Feedback Session'}
                    {padCompletion.isSaving
                      ? <ButtonSpinner className="w-5 h-5" />
                      : <CheckCircleIcon className="w-5 h-5" aria-hidden="true" />}
                  </button>
                );
              })() : undefined}
            />
          )}
        </div>
      </div>

      <GenerateReportProgressDialog
        // Display-only label for the simulated progress dialog — real LLM
        // generation uses AUTISM_PAD_REPORT_STAGE ("Autism Post-Assessment Discussion").
        reportStage={AUTISM_PAD_DISPLAY_NAME}
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        handleNextPageButtonClick={handleAfterGeneration}
        isGenerating={padCompletion.isGenerating}
        onCancel={() => padCompletion.setIsGenerating(false)}
        completionButtonLabel="Go to Documents"
      />
    </Layout>
  );
}
